import { useEffect, useState } from 'react';
import { Flex, Checkbox, useMediaQuery } from '@chakra-ui/react';
import BEIcon from '@molecules/BEIcon';
import BEButton from '@molecules/Button/Button';
import Text from '@molecules/Text';
import ScreenSize from '../../../constants/screen-sizes';
import { StyledSection, StyledColumnWrapper } from './StyledCreditCardForm';
import { useTranslation } from 'react-i18next';

import countryData from '@data/data/countries.json';
import { UseFormResult } from '@molecules/FormFactory/useForm';
import { FormValidation } from '@data/other/validation';
import CreditCard from '@data/models/CreditCard';
import { DropdownOptionItem } from '@molecules/FormFactory/Forms/Dropdown/DropdownOption/DropdownOptionProps';
import { CARD_TYPES, getCreditCardType } from '@business/utils/creditcard';
import { useWatch } from 'react-hook-form';

const STATE_OPTIONS = [];

const COUNTRY_OPTIONS: DropdownOptionItem[] = countryData.map((country) => ({
  id: country.code,
  text: country.name,
  value: country.code,
}));

interface CreditCardFormProps {
  requireCvv: boolean;
  defaultValues?: CreditCard;
  useFormResult: UseFormResult<CreditCard>;
}

type CardInputProps = {
  useFormResult: UseFormResult<CreditCard>;
  onCardTypeChange: (type: CARD_TYPES) => void;
};

const CardNumberWatcher = ({
  useFormResult,
  onCardTypeChange,
}: CardInputProps) => {
  const { formRef } = useFormResult;
  const cardNumber = useWatch({
    control: formRef.current.control,
    name: 'credit-card',
  });

  useEffect(() => {
    if (cardNumber) {
      onCardTypeChange(getCreditCardType(cardNumber) || CARD_TYPES.UNKNOWN);
    } else {
      onCardTypeChange(CARD_TYPES.UNKNOWN);
    }
  }, [cardNumber, onCardTypeChange]);

  return null;
};

const validation: FormValidation<CreditCard> = {
  ...CreditCard.validationModel,
};

const CreditCardForm = ({
  requireCvv = true,
  defaultValues,
  useFormResult,
}: CreditCardFormProps) => {
  const { t } = useTranslation();
  const [isMobile] = useMediaQuery(`(max-width: ${ScreenSize.MOBILE_LARGE})`);
  const { Form, Input, Dropdown } = useFormResult;
  const [currentCardType, setCurrentCardType] = useState(CARD_TYPES.UNKNOWN);

  return (
    <Form defaultValues={defaultValues} validation={validation}>
      <Flex gap="6" direction="column" pt="2">
        <Flex
          gap={isMobile ? '6' : '4'}
          direction={isMobile ? 'column' : 'row'}
        >
          <Input.Text
            name="credit-card"
            label={t('card-number')}
            mask={
              currentCardType === CARD_TYPES.AMEX
                ? '9999 999999 99999'
                : '9999 9999 9999 9999'
            }
            type="maskedtext"
          />
          <CardNumberWatcher
            useFormResult={useFormResult}
            onCardTypeChange={setCurrentCardType}
          />
          <Flex gap={4}>
            <Input.Text
              name="expiration-date"
              label={t('MM/YYYY')}
              mask="99/99"
              type="maskedtext"
            />
            {requireCvv && <Input.Text name="cvc" label="CVC" type="number" />}
          </Flex>
        </Flex>
        <Input.Text label="Cardholder Name" name="cardholder" />
        <StyledSection size="lg" weight="600">
          {t('address-details')}
        </StyledSection>
        <Checkbox>Same as address</Checkbox>
        <Input.Text label={t('street-address')} name="address" />
        <Input.Text
          label={t('apt-suite-floor-etc')}
          name="apt-suite-floor-etc"
        />
        <Dropdown
          name="country"
          label={t('country')}
          options={COUNTRY_OPTIONS}
          placeholder={t('select-country')}
        />
        <Flex
          gap={isMobile ? '6' : '4'}
          direction={isMobile ? 'column' : 'row'}
        >
          <Dropdown
            name="state"
            label={t('state')}
            labelPosition="default"
            options={STATE_OPTIONS}
            placeholder={t('select-state')}
          />
          <Input.Text name="city" label={t('city')} />
        </Flex>
        <StyledColumnWrapper>
          <Input.Text name="zip-code" label={t('zip-postal-code')} />
        </StyledColumnWrapper>
        <Input.Text name="billing-email" label={t('billing-email')} />
        <Flex gap={1}>
          <BEIcon name="shield-check" size="lg" />
          <Text>{t('secure-online-payment')}</Text>
          <BEButton color="black" variant="link">
            {t('learn-more')}
          </BEButton>
        </Flex>
      </Flex>
    </Form>
  );
};

export default CreditCardForm;
